import { inject, Injectable, OnDestroy } from '@angular/core';
import { Analytics } from '@core/services/analytics';

type Unlisten = () => void;
type PageContext = 'all_problems' | 'problem';

@Injectable({ providedIn: 'root' })
export class ProctoringService implements OnDestroy {
  private unlisteners: Unlisten[] = [];
  private started = false;

  // ---- Tab switch stats ----
  private switchCount = 0;
  private lastOutAt: number | null = null;
  private totalAwayMs = 0;
  private minGapMs = 800;
  private lastEventAt = 0;

  // ---- Copy/Paste stats ----
  private copyTotals = { copy: 0, paste: 0 };
  private perProblem: Record<string, { copy: number; paste: number }> = {};

  // Dedupe ctrl/cmd + C/V vs. 'copy'/'paste' events
  private lastActionType: 'copy' | 'paste' | null = null;
  private lastActionTs = 0;
  private actionGapMs = 400;

  // ---- Context ----
  private currentPage: PageContext | null = null;
  private currentTestSetId: string | null = null;
  private currentProblemId: string | null = null;

  private analytics = inject(Analytics);

  setContext(page: PageContext, testSetId?: string | null): void {
    this.currentPage = page;
    this.currentTestSetId = testSetId ?? null;
  }

  setCurrentProblemId(problemId: string | null): void {
    this.currentProblemId = problemId;
    if (problemId && !this.perProblem[problemId]) {
      this.perProblem[problemId] = { copy: 0, paste: 0 };
    }
  }

  private base() {
    return {
      page: this.currentPage,
      test_set_id: this.currentTestSetId,
      problem_id: this.currentProblemId,
    };
  }

  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;

    // ---- Tab switch listeners ----
    const onVisibility = () => {
      if (document.visibilityState === 'hidden')
        this.switchOut('visibility_hidden');
      else this.switchIn('visibility_visible');
    };
    const onBlur = () => this.switchOut('window_blur');
    const onFocus = () => this.switchIn('window_focus');
    const onPageHide = () => this.switchOut('pagehide');

    document.addEventListener('visibilitychange', onVisibility, {
      passive: true,
    });
    window.addEventListener('blur', onBlur, { passive: true });
    window.addEventListener('focus', onFocus, { passive: true });
    window.addEventListener('pagehide', onPageHide, { passive: true });

    this.unlisteners.push(() =>
      document.removeEventListener('visibilitychange', onVisibility)
    );
    this.unlisteners.push(() => window.removeEventListener('blur', onBlur));
    this.unlisteners.push(() => window.removeEventListener('focus', onFocus));
    this.unlisteners.push(() =>
      window.removeEventListener('pagehide', onPageHide)
    );

    // ---- Copy/Paste listeners (mouse/OS + shortcuts) ----
    const onCopy = () => this.handleCopyPaste('copy', 'dom');
    const onPaste = () => this.handleCopyPaste('paste', 'dom');
    const onKeydown = (e: KeyboardEvent) => {
      const isMeta = e.ctrlKey || e.metaKey;
      if (!isMeta) return;
      const k = e.key.toLowerCase();
      if (k === 'c') this.handleCopyPaste('copy', 'kbd');
      if (k === 'v') this.handleCopyPaste('paste', 'kbd');
    };

    document.addEventListener('copy', onCopy, { passive: true, capture: true });
    document.addEventListener('paste', onPaste, {
      passive: true,
      capture: true,
    });
    window.addEventListener('keydown', onKeydown, { passive: true });

    this.unlisteners.push(() =>
      document.removeEventListener('copy', onCopy, true)
    );
    this.unlisteners.push(() =>
      document.removeEventListener('paste', onPaste, true)
    );
    this.unlisteners.push(() =>
      window.removeEventListener('keydown', onKeydown)
    );

    // ---- Initial snapshots ----
    this.analytics.trackUserBehavior('tab_switch', {
      ...this.base(),
      reason: 'proctoring_start',
      visibilityState: document.visibilityState,
      hasFocus: document.hasFocus?.(),
      switch_count: this.switchCount,
      total_away_ms: this.totalAwayMs,
      ts: new Date().toISOString(),
    });

    this.analytics.trackUserBehavior('copy_paste', {
      ...this.base(),
      phase: 'start',
      total_copy: this.copyTotals.copy,
      total_paste: this.copyTotals.paste,
      ts: new Date().toISOString(),
    });
  }

  stop(): void {
    // Summaries
    this.analytics.trackExamEvent('tab_switch_summary', {
      ...this.base(),
      switch_count: this.switchCount,
      total_away_ms: this.totalAwayMs,
      currently_out: this.lastOutAt !== null,
      ts: new Date().toISOString(),
    });

    this.analytics.trackExamEvent('copy_paste_summary', {
      ...this.base(),
      total_copy: this.copyTotals.copy,
      total_paste: this.copyTotals.paste,
      per_problem: this.perProblem,
      ts: new Date().toISOString(),
    });

    while (this.unlisteners.length) this.unlisteners.pop()?.();
    this.started = false;
    this.lastOutAt = null;
  }

  ngOnDestroy(): void {
    if (this.started) this.stop();
  }

  // ---- Tab switch core ----
  private switchOut(reason: string) {
    const now = Date.now();
    if (now - this.lastEventAt < this.minGapMs) return;
    this.lastEventAt = now;

    if (this.lastOutAt !== null) return;
    this.lastOutAt = now;
    this.switchCount += 1;

    this.analytics.trackUserBehavior('tab_switch', {
      ...this.base(),
      direction: 'out',
      reason,
      switch_count: this.switchCount,
      total_away_ms: this.totalAwayMs,
      ts: new Date(now).toISOString(),
    });
  }

  private switchIn(reason: string) {
    const now = Date.now();
    if (now - this.lastEventAt < this.minGapMs) return;
    this.lastEventAt = now;

    if (this.lastOutAt === null) {
      this.analytics.trackUserBehavior('tab_switch', {
        ...this.base(),
        direction: 'in',
        reason,
        duration_ms: 0,
        switch_count: this.switchCount,
        total_away_ms: this.totalAwayMs,
        ts: new Date(now).toISOString(),
      });
      return;
    }

    const durationMs = now - this.lastOutAt;
    this.totalAwayMs += durationMs;
    this.lastOutAt = null;

    this.analytics.trackUserBehavior('tab_switch', {
      ...this.base(),
      direction: 'in',
      reason,
      duration_ms: durationMs,
      switch_count: this.switchCount,
      total_away_ms: this.totalAwayMs,
      ts: new Date(now).toISOString(),
    });
  }

  // ---- Copy/Paste core (with dedupe) ----
  private handleCopyPaste(action: 'copy' | 'paste', source: 'dom' | 'kbd') {
    const now = Date.now();
    if (
      this.lastActionType === action &&
      now - this.lastActionTs < this.actionGapMs
    ) {
      return; // dedupe same action fired twice (keydown + clipboard event)
    }
    this.lastActionType = action;
    this.lastActionTs = now;

    this.copyTotals[action] += 1;

    if (this.currentProblemId) {
      const bucket = (this.perProblem[this.currentProblemId] ??= {
        copy: 0,
        paste: 0,
      });
      bucket[action] += 1;
    }

    this.analytics.trackUserBehavior('copy_paste', {
      ...this.base(),
      action,
      source,
      total_copy: this.copyTotals.copy,
      total_paste: this.copyTotals.paste,
      ts: new Date().toISOString(),
    });
  }
}
